import { DragHandle, ViewColumn } from '@mui/icons-material';
import {
    Button,
    TextField,
    Menu,
    Switch,
    FormControlLabel,
    ListItem,
    List,
    Badge,
    Tooltip,
    Box,
} from '@mui/material';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataTableColumn } from '../../DataTable';
import styles from './GridToolbarColumnsButton.module.scss';
import _ from 'lodash';
import { useAuth } from 'hooks';
import { mapLocalSessionToFb } from '../../opsMapping';
import { updateUserSession } from 'lib/firebase';
import { UserSessionKeys } from 'types/User/firebaseTypes';
import { activateSnackbar } from 'store/features/ui';
import { DataTableReportPreset } from '../GridToolbarPresetSelector/GridToolbarPresetSelector';
import { useDispatch } from 'react-redux';

export interface GridToolbarColumnsButtonProps {
    gridId: UserSessionKeys;
    columnVisibilityModel: Record<string, boolean>;
    setColumnVisibilityModel: React.Dispatch<
        React.SetStateAction<GridColumnVisibilityModel>
    >;
    columnSortingOrder: string[];
    setColumnSortingOrder: React.Dispatch<React.SetStateAction<string[]>>;
    columns: DataTableColumn[];
    defaultColumnSortingOrder?: string[];
    currentPreset: DataTableReportPreset | null;
}

/**
 * Toolbar component for the DataTable's toolbar for column reorganization and visibility toggling.
 * @param {string} id - column id for our internal sorting and visibility models. Must be provided for them to work properly with local storage.
 * @param {string[]} defaultColumnSortingOrder - optional default sorting order to revert to when resetting columns. NOTE: If this is not provided, resetting the columns is not possible
 */
export function GridToolbarColumnsButton({
    gridId,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnSortingOrder,
    setColumnSortingOrder,
    defaultColumnSortingOrder,
    columns,
    currentPreset,
}: GridToolbarColumnsButtonProps) {
    const [currentDragId, setCurrentDragId] = useState('');
    const [columnSearchText, setColumnSearchText] = useState('');
    const [open, setOpen] = useState(false);
    const { user } = useAuth();
    const dispatch = useDispatch();

    const buttonRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(this: Document, event: MouseEvent) {
            if (
                panelRef.current &&
                !panelRef.current.contains(event.target as Node)
            ) {
                setColumnVisibilityModel({ ...columnVisibilityModel });
                setColumnSortingOrder(columnSortingOrder);
                setOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on dismount
            document.removeEventListener('mousedown', handleClickOutside);
        };
    });

    const handleModelSave = (
        newSortingOrder?: string[],
        newModel?: Record<string, boolean>
    ) => {
        if (user?.uid) {
            const newSession = mapLocalSessionToFb(
                {
                    sortingOrder: newSortingOrder ?? columnSortingOrder,
                    visibilityModel: newModel ?? columnVisibilityModel,
                },
                columns
            );
            updateUserSession(user.uid, gridId, newSession);
        }
    };

    const handleReset = () => {
        const defaultColumnVisibilityModel: Record<string, boolean> =
            columns.reduce(
                (prev, curr) => ({
                    ...prev,
                    [curr.field]: curr.hideByDefault ? false : true,
                }),
                {}
            );

        if (user?.uid) {
            const newSession = mapLocalSessionToFb(
                {
                    sortingOrder: defaultColumnSortingOrder ?? [],
                    visibilityModel: defaultColumnVisibilityModel,
                },
                columns
            );
            updateUserSession(user.uid, gridId, newSession);
        }

        if (defaultColumnSortingOrder) {
            setColumnSortingOrder(() => [...defaultColumnSortingOrder]);
        }
        setColumnVisibilityModel(defaultColumnVisibilityModel);
        handleModelSave(
            defaultColumnSortingOrder,
            defaultColumnVisibilityModel
        );
    };

    const handleToggleAll = (visibility: boolean) => {
        let newModel = {};
        columns.forEach(
            ({ field }) => (newModel = { ...newModel, [field]: visibility })
        );
        setColumnVisibilityModel(newModel);
        handleModelSave(undefined, newModel);
    };

    const handleDrag = (e: React.DragEvent) => {
        setCurrentDragId(e.currentTarget.id);
    };

    const handleDrop = (e: React.DragEvent) => {
        const newSortingOrder = columnSortingOrder;

        const draggingElemIndex = newSortingOrder.findIndex(
            (val) => val === currentDragId
        );
        const droppingElemIndex = newSortingOrder.findIndex(
            (val) => val === e.currentTarget.id
        );

        const movingElem = newSortingOrder.splice(draggingElemIndex, 1)[0];

        newSortingOrder.splice(droppingElemIndex, 0, movingElem);

        setColumnSortingOrder([...newSortingOrder]);
        handleModelSave(newSortingOrder);
    };

    const mappedColumns = useMemo(() => {
        return columns
            .filter((c) => {
                const columnName = c.headerName?.toLowerCase();
                return (
                    !columnSearchText.length ||
                    (columnName && columnName.includes(columnSearchText))
                );
            })
            .sort((a, b) => {
                if (columnSortingOrder) {
                    const columnAIndex = columnSortingOrder?.findIndex(
                        (value) => value === a.field
                    );
                    const columnBIndex = columnSortingOrder?.findIndex(
                        (value) => value === b.field
                    );

                    return columnAIndex > columnBIndex ? 1 : -1;
                }

                return 0;
            });
    }, [columns, columnSortingOrder, columnSearchText]);

    const [columnError, setColumnError] = useState(false);

    // If all columns are turned off, show a warning because the table will look really broken.
    useEffect(() => {
        setColumnError(
            Object.values(columnVisibilityModel).filter((item) => item === true)
                .length === 0
        );
    }, [columnVisibilityModel]);

    return (
        <>
            <Tooltip
                title={
                    columnError
                        ? 'No columns are set to be visible, so the table may not be showing any data. To fix the table, turn on at least one column.'
                        : null
                }
            >
                <Button
                    size="small"
                    startIcon={<ViewColumn fontSize="small" />}
                    id="basic-button"
                    aria-controls={open ? 'basic-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={() => setOpen(!open)}
                    ref={buttonRef}
                >
                    <Badge
                        badgeContent={
                            columnError ? (
                                <span className={styles.noColumnWarning}>
                                    !
                                </span>
                            ) : null
                        }
                        color="error"
                    >
                        <Box sx={{ paddingRight: '10px' }}>Columns</Box>
                    </Badge>
                </Button>
            </Tooltip>
            <Menu
                open={open}
                onClose={() => setOpen(!open)}
                anchorEl={buttonRef.current}
                className={styles.menu}
                PaperProps={{
                    style: {
                        maxHeight: '40rem',
                    },
                }}
            >
                <ListItem>
                    <TextField
                        variant="standard"
                        className={styles.columnSearch}
                        label="Find Column"
                        value={columnSearchText}
                        onChange={(e) => {
                            setColumnSearchText(e.target.value.toLowerCase());
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </ListItem>
                <List className={styles.menuList}>
                    {mappedColumns.map(({ field, headerName }) => {
                        const isChecked = !!columnVisibilityModel[field];

                        return (
                            <ListItem
                                id={field}
                                key={field}
                                className={styles.listItem}
                                onDragOver={(e) => e.preventDefault()}
                                onDragStart={handleDrag}
                                onDrop={handleDrop}
                                draggable
                            >
                                <DragHandle />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isChecked}
                                            onChange={() => {
                                                if (!currentPreset) {
                                                    const newModel = {
                                                        ...columnVisibilityModel,
                                                        [field]: !isChecked,
                                                    };
                                                    handleModelSave(
                                                        undefined,
                                                        newModel
                                                    );
                                                    setColumnVisibilityModel(
                                                        newModel
                                                    );
                                                } else {
                                                    dispatch(
                                                        activateSnackbar({
                                                            message:
                                                                'Please disable current preset before changing columns',
                                                            severity: 'error',
                                                        })
                                                    );
                                                }
                                            }}
                                        />
                                    }
                                    label={headerName}
                                />
                            </ListItem>
                        );
                    })}
                </List>
                <ListItem>
                    <Button onClick={() => handleToggleAll(true)}>
                        Show All
                    </Button>
                    <Button onClick={() => handleToggleAll(false)}>
                        Hide All
                    </Button>
                    {defaultColumnSortingOrder && (
                        <Button onClick={handleReset}>Reset</Button>
                    )}
                </ListItem>
            </Menu>
        </>
    );
}
